import { useCallback, useState, useEffect } from "react";
import Navbar from "./components/Navbar";
import HeroSection from "./components/HeroSection";
import WhatsIncludedSection from "./components/WhatsIncludedSection";
import WhyWeExistSection from "./components/WhyWeExistSection";
import SpacesSection from "./components/SpacesSection";
import ApplicationSection from "./components/ApplicationSection";
import RulesSection from "./components/RulesSection";
import OurMissionSection from "./components/OurMissionSection";
import FAQSection from "./components/FAQSection";
import ContactSection from "./components/ContactSection";
import MeetOurMakersSection from "./components/MeetOurMakersSection";
import Footer from "./components/Footer";
import AdminSaveBar from "@/components/feature/AdminSaveBar";
import SectionVisibilityWrapper from "@/components/feature/SectionVisibilityWrapper";
import PreviewModeBar from "@/components/feature/PreviewModeBar";
import SEOHead from "@/components/feature/SEOHead";
import InstagramFeedSection from "@/components/feature/InstagramFeedSection";
import { useDarkMode } from "@/context/DarkModeContext";
import { useAdmin } from "@/context/AdminContext";
import { stagingStoreGet, stagingStoreSet, stagingStoreNotify } from "@/hooks/useAdminStorage";
import { useAdminStorage } from "@/hooks/useAdminStorage";
import {
  defaultHero,
  defaultWhatsIncluded,
  defaultSpacesSection,
  defaultApplication,
  defaultRules,
  defaultMission,
  defaultMeetMakers,
  defaultFAQ,
  defaultContact,
  defaultSectionLabels,
  defaultInstagramPosts,
  defaultWhyWeExist,
  type FAQSectionData,
} from "@/data/siteContent";

// ─── Section registry ────────────────────────────────────────────────────────

type SectionType = "hero" | "whatsIncluded" | "whyWeExist" | "spaces" | "application" | "rules" | "mission" | "meetMakers" | "faq" | "contact" | "instagramFeed";

// Maps section key → base storage key used by that component
const SECTION_BASE_KEYS: Record<string, string> = {
  hero: "ma_hero",
  whatsIncluded: "ma_whats_included",
  whyWeExist: "ma_why_we_exist",
  spaces: "ma_spaces_v2",
  application: "ma_application_v2",
  rules: "ma_rules",
  mission: "ma_mission",
  meetMakers: "ma_meet_makers_header",
  faq: "ma_faq_v2",
  contact: "ma_contact_v3",
  instagramFeed: "ma_instagram_posts",
};

const ADDABLE_SECTIONS: { key: SectionType; label: string; icon: string; desc: string }[] = [
  { key: "hero", label: "Hero", icon: "ri-home-smile-line", desc: "Full-width hero with headline and CTA" },
  { key: "whatsIncluded", label: "What's Included", icon: "ri-gift-line", desc: "Feature cards grid" },
  { key: "whyWeExist", label: "Why We Exist", icon: "ri-heart-3-line", desc: "Mission statement with quote and values" },
  { key: "spaces", label: "Spaces", icon: "ri-layout-grid-line", desc: "Space options with pricing" },
  { key: "application", label: "Artist Application", icon: "ri-file-list-line", desc: "Application form section" },
  { key: "rules", label: "Rules", icon: "ri-shield-check-line", desc: "Rules & guidelines" },
  { key: "mission", label: "Our Mission", icon: "ri-heart-line", desc: "Founder story & mission" },
  { key: "meetMakers", label: "Meet Our Makers", icon: "ri-group-line", desc: "Artist gallery" },
  { key: "faq", label: "FAQ", icon: "ri-question-line", desc: "FAQ accordion" },
  { key: "contact", label: "Contact", icon: "ri-mail-line", desc: "Contact form & info" },
  { key: "instagramFeed", label: "Instagram Feed", icon: "ri-instagram-line", desc: "Embedded Instagram posts" },
];

// ─── Section renderer ─────────────────────────────────────────────────────────

interface SectionRendererProps {
  sectionKey: string;
  dataKey: string;
}

function SectionRenderer({ sectionKey, dataKey }: SectionRendererProps) {
  // Determine the base type from the section key (strip copy suffix)
  const baseType = sectionKey.replace(/-copy-[a-z0-9]+$/, "").replace(/-[a-z0-9]{4}$/, "") as SectionType;

  switch (baseType) {
    case "hero": return <HeroSection dataKey={dataKey} />;
    case "whatsIncluded": return <WhatsIncludedSection dataKey={dataKey} />;
    case "whyWeExist": return <WhyWeExistSection dataKey={dataKey} />;
    case "spaces": return <SpacesSection dataKey={dataKey} />;
    case "application": return <ApplicationSection dataKey={dataKey} />;
    case "rules": return <RulesSection dataKey={dataKey} />;
    case "mission": return <OurMissionSection dataKey={dataKey} />;
    case "meetMakers": return <MeetOurMakersSection dataKey={dataKey} />;
    case "faq": return <FAQSection dataKey={dataKey} />;
    case "contact": return <ContactSection dataKey={dataKey} />;
    case "instagramFeed": return <InstagramFeedSection dataKey={dataKey} />;
    default: return null;
  }
}

// ─── Deep copy data into a new staging store key (NOT localStorage) ─────────

function cloneDataToNewKey(sourceKey: string, destKey: string): void {
  try {
    // First check staging store (unsaved changes take priority)
    const staged = stagingStoreGet(sourceKey);
    if (staged !== undefined) {
      stagingStoreSet(destKey, JSON.parse(JSON.stringify(staged)));
      stagingStoreNotify(destKey);
      return;
    }
    // Fall back to localStorage
    const raw = localStorage.getItem(sourceKey);
    if (raw) {
      const parsed = JSON.parse(raw);
      stagingStoreSet(destKey, parsed);
      stagingStoreNotify(destKey);
    }
  } catch { /* ignore */ }
}

function getDefaultDataForSection(type: SectionType): unknown {
  switch (type) {
    case "hero": return JSON.parse(JSON.stringify(defaultHero));
    case "whatsIncluded": return JSON.parse(JSON.stringify(defaultWhatsIncluded));
    case "whyWeExist": return JSON.parse(JSON.stringify(defaultWhyWeExist));
    case "spaces": return JSON.parse(JSON.stringify(defaultSpacesSection));
    case "application": return JSON.parse(JSON.stringify(defaultApplication));
    case "rules": return JSON.parse(JSON.stringify(defaultRules));
    case "mission": return JSON.parse(JSON.stringify(defaultMission));
    case "meetMakers": return JSON.parse(JSON.stringify(defaultMeetMakers));
    case "faq": return JSON.parse(JSON.stringify(defaultFAQ));
    case "contact": return JSON.parse(JSON.stringify(defaultContact));
    case "instagramFeed": return JSON.parse(JSON.stringify(defaultInstagramPosts));
    default: return {};
  }
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function HomePage() {
  const { isDark } = useDarkMode();
  const { isAdmin, isEditing, sectionOrder, setSectionOrder, sectionDataMap, setSectionDataMap, isPreviewMode } = useAdmin();
  const [addSectionAfter, setAddSectionAfter] = useState<string | null>(null);

  // Cloud-synced section labels (persists renames across devices)
  const [sectionLabels, setSectionLabels] = useAdminStorage<Record<string, string>>("ma_section_labels", defaultSectionLabels);

  // Read live FAQ data so the JSON-LD schema reflects user edits, not hardcoded defaults
  const [liveFAQ] = useAdminStorage<FAQSectionData>("ma_faq_v2", defaultFAQ);

  // Hash-based scroll to sections
  useEffect(() => {
    const scrollToHash = () => {
      const hash = window.location.hash;
      if (!hash) return;
      const id = hash.replace("#", "");
      if (!id) return;
      // Small delay to ensure DOM is settled after sections load
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          const navOffset = 80; // account for sticky navbar
          const top = el.getBoundingClientRect().top + window.scrollY - navOffset;
          window.scrollTo({ top, behavior: "smooth" });
        } else if (id === "whats-included") {
          // Backward compatibility for old anchor links
          const fallback = document.getElementById("whatsIncluded");
          if (fallback) {
            const navOffset = 80;
            const top = fallback.getBoundingClientRect().top + window.scrollY - navOffset;
            window.scrollTo({ top, behavior: "smooth" });
          }
        }
      }, 150);
    };
    scrollToHash();
    window.addEventListener("hashchange", scrollToHash);
    return () => window.removeEventListener("hashchange", scrollToHash);
  }, [sectionOrder]);

  // Get the label for a section key (use stored label, or base type label, or key itself)
  const getLabel = useCallback((sectionKey: string): string => {
    if (sectionLabels[sectionKey]) return sectionLabels[sectionKey];
    const baseType = sectionKey.replace(/-copy-[a-z0-9]+$/, "").replace(/-[a-z0-9]{4}$/, "");
    return defaultSectionLabels[baseType] ?? sectionKey;
  }, [sectionLabels]);

  const handleLabelChange = useCallback((sectionKey: string, newLabel: string) => {
    setSectionLabels({ ...sectionLabels, [sectionKey]: newLabel });
  }, [sectionLabels, setSectionLabels]);

  // Get the actual storage key for a section (may be remapped for duplicates)
  const getDataKey = useCallback((sectionKey: string): string => {
    if (sectionDataMap[sectionKey]) return sectionDataMap[sectionKey];
    // For base sections, use the canonical key
    const baseType = sectionKey.replace(/-copy-[a-z0-9]+$/, "").replace(/-[a-z0-9]{4}$/, "");
    return SECTION_BASE_KEYS[baseType] ?? SECTION_BASE_KEYS[sectionKey] ?? sectionKey;
  }, [sectionDataMap]);

  const moveSection = useCallback((key: string, dir: -1 | 1) => {
    const newOrder = [...sectionOrder];
    const idx = newOrder.indexOf(key);
    if (idx === -1) return;
    const target = idx + dir;
    if (target < 0 || target >= newOrder.length) return;
    [newOrder[idx], newOrder[target]] = [newOrder[target], newOrder[idx]];
    setSectionOrder(newOrder);
  }, [sectionOrder, setSectionOrder]);

  const duplicateSection = useCallback((key: string) => {
    const idx = sectionOrder.indexOf(key);
    if (idx === -1) return;

    // Create a unique key for the duplicate
    const suffix = Date.now().toString(36).slice(-4);
    const newKey = `${key}-copy-${suffix}`;

    // Create a unique storage key for the duplicate's data
    const sourceDataKey = getDataKey(key);
    const newDataKey = `${sourceDataKey}-copy-${suffix}`;

    // Deep copy the source data into a NEW staging store key
    cloneDataToNewKey(sourceDataKey, newDataKey);

    // Register the data key mapping
    const newMap = { ...sectionDataMap, [newKey]: newDataKey };
    setSectionDataMap(newMap);

    // Name the new section so the user can identify it (persisted)
    const baseLabel = getLabel(key);
    const newLabel = `${baseLabel} (Copy)`;
    setSectionLabels({ ...sectionLabels, [newKey]: newLabel });

    // Insert into order
    const newOrder = [...sectionOrder];
    newOrder.splice(idx + 1, 0, newKey);
    setSectionOrder(newOrder);
  }, [sectionOrder, setSectionOrder, sectionDataMap, setSectionDataMap, getDataKey, getLabel, sectionLabels, setSectionLabels]);

  const addSection = useCallback((afterKey: string, newSectionType: SectionType) => {
    const idx = sectionOrder.indexOf(afterKey);
    if (idx === -1) return;

    const suffix = Date.now().toString(36).slice(-4);
    const uniqueKey = `${newSectionType}-${suffix}`;
    const newDataKey = `ma_${newSectionType}-new-${suffix}`;

    try {
      const defaultData = getDefaultDataForSection(newSectionType);
      stagingStoreSet(newDataKey, defaultData);
      stagingStoreNotify(newDataKey);
    } catch { /* ignore */ }

    const newMap = { ...sectionDataMap, [uniqueKey]: newDataKey };
    setSectionDataMap(newMap);

    // Name the new section so the user can identify it (persisted)
    const addedLabel = ADDABLE_SECTIONS.find((s) => s.key === newSectionType)?.label ?? newSectionType;
    setSectionLabels({ ...sectionLabels, [uniqueKey]: `${addedLabel} (New)` });

    const newOrder = [...sectionOrder];
    newOrder.splice(idx + 1, 0, uniqueKey);
    setSectionOrder(newOrder);
    setAddSectionAfter(null);
  }, [sectionOrder, setSectionOrder, sectionDataMap, setSectionDataMap, sectionLabels, setSectionLabels]);

  const domain = import.meta.env.VITE_SITE_URL as string | undefined;
  const canonical = domain ? `${domain}/` : "https://makersalley.co/";

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${canonical}#business`,
    name: "Makers Alley Co",
    alternateName: "Makers Alley",
    description: "A curated retail space for independent artists and makers in Medford, Oregon. Rent shelf or wall display space to sell handmade goods year-round. Artists keep 100% of their sales. Includes POS system, sales staff, utilities, and transaction fee coverage.",
    url: domain ?? "https://makersalley.co",
    email: "info@makersalley.co",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Medford",
      addressRegion: "OR",
      addressCountry: "US",
      description: "Coming Soon — Rogue Valley, Medford, Oregon",
    },
    geo: { "@type": "GeoCoordinates", latitude: 42.3265, longitude: -122.8756 },
    areaServed: [
      { "@type": "City", name: "Medford", containedInPlace: { "@type": "State", name: "Oregon" } },
      { "@type": "City", name: "Ashland" },
      { "@type": "City", name: "Jacksonville" },
      { "@type": "City", name: "Central Point" },
      { "@type": "AdministrativeArea", name: "Rogue Valley" },
      { "@type": "Country", name: "United States" },
    ],
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      description: "Opening hours to be announced. Coming soon to Medford, Oregon.",
    },
    priceRange: "$$",
    currenciesAccepted: "USD",
    paymentAccepted: "Credit Card, Debit Card",
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Artist Shelf & Display Rentals",
      itemListElement: [
        { "@type": "Offer", name: "Showcase Shelf", price: "75", priceCurrency: "USD", description: "Entry-level shelf rental for artists starting out" },
        { "@type": "Offer", name: "Standard Shelf", price: "120", priceCurrency: "USD", description: "Mid-size shelf for established makers" },
        { "@type": "Offer", name: "Premium Shelf", price: "180", priceCurrency: "USD", description: "Large shelf for high-volume artists" },
        { "@type": "Offer", name: "Standard Wall Display", price: "150", priceCurrency: "USD", description: "Wall-mounted display space" },
        { "@type": "Offer", name: "Premium Wall Display", price: "220", priceCurrency: "USD", description: "Premium wall display for featured artists" },
      ],
    },
    sameAs: [
      "https://www.instagram.com/makersalleyco",
      "https://www.facebook.com/makersalleyco",
    ],
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${canonical}#website`,
    url: domain ?? "https://makersalley.co",
    name: "Makers Alley Co",
    description: "A permanent retail home for handmade creators. Year-round visibility and a community-driven space for artists.",
    publisher: { "@id": `${canonical}#business` },
  };

  const storeSchema = {
    "@context": "https://schema.org",
    "@type": "Store",
    "@id": `${canonical}#store`,
    name: "Makers Alley Co",
    url: domain ?? "https://makersalley.co",
    description: "Artist retail space and marketplace for handmade goods in Medford, Oregon.",
    image: "",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Medford",
      addressRegion: "OR",
      addressCountry: "US",
    },
    geo: { "@type": "GeoCoordinates", latitude: 42.3265, longitude: -122.8756 },
    telephone: "",
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${canonical}#faq`,
    mainEntity: liveFAQ.items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  return (
    <>
      <SEOHead
        title="Makers Alley Co — A Home for Handmade Artists"
        description="A permanent retail home for handmade creators. Makers Alley Co offers year‑round visibility, easy setup, and a community‑driven space where artists from anywhere can share their work with shoppers every day."
        keywords="handmade artists, maker marketplace, year‑round retail space, artist shelf rentals, sell handmade art, indie creators, artisan shop, artist alley, Medford Oregon retail, artist displays, community"
        canonicalPath="/"
        ogType="website"
        ogTitle="Makers Alley Co — A Home for Handmade Artists"
        ogDescription="A permanent retail home for handmade creators. Makers Alley Co offers year‑round visibility, easy setup, and a community‑driven space where artists from anywhere can share their work with shoppers every day."
        jsonLd={[websiteSchema, localBusinessSchema, storeSchema, faqSchema]}
        lastModified={new Date().toISOString().slice(0, 10)}
        author="Makers Alley Co"
      />
      <div
        className={`min-h-screen transition-colors duration-300 ${isDark ? "dark bg-gray-950 text-gray-100" : "bg-white text-gray-900"}`}
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        <Navbar />

        <PreviewModeBar hint="Hover sections to reorder, duplicate, or add new sections" />

        <main className={`${isAdmin ? (isPreviewMode ? "pt-10" : "pb-36") : "pb-16"}`}>
          {sectionOrder.map((key, idx) => {
            const dataKey = getDataKey(key);
            return (
        <SectionVisibilityWrapper
                key={key}
                sectionKey={key}
                label={getLabel(key)}
                isFirst={idx === 0}
                isLast={idx === sectionOrder.length - 1}
                onMoveUp={() => moveSection(key, -1)}
                onMoveDown={() => moveSection(key, 1)}
                onDuplicateSection={() => duplicateSection(key)}
                onAddSection={() => setAddSectionAfter(key)}
                onLabelChange={(newLabel) => handleLabelChange(key, newLabel)}
              >
                <SectionRenderer sectionKey={key} dataKey={dataKey} />
              </SectionVisibilityWrapper>
            );
          })}
        </main>

        <Footer />
        <AdminSaveBar />

        {/* Add Section Modal */}
        {addSectionAfter && isEditing && (
          <div
            className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50"
            onClick={() => setAddSectionAfter(null)}
          >
            <div
              className={`rounded-2xl border p-6 w-full max-w-lg mx-4 ${isDark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className={`font-bold text-base ${isDark ? "text-white" : "text-gray-900"}`}>
                  Add a Section
                </h3>
                <button
                  onClick={() => setAddSectionAfter(null)}
                  className={`w-8 h-8 flex items-center justify-center rounded-full cursor-pointer transition-colors ${isDark ? "hover:bg-gray-700 text-gray-400" : "hover:bg-gray-100 text-gray-500"}`}
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
              <p className={`text-xs mb-4 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                Choose a section type to add after <strong>{getLabel(addSectionAfter) ?? addSectionAfter}</strong>. The new section will be completely independent — editing it won&apos;t affect the original.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {ADDABLE_SECTIONS.map((s) => (
                  <button
                    key={s.key}
                    onClick={() => addSection(addSectionAfter, s.key)}
                    className={`flex items-start gap-3 px-3 py-3 rounded-xl border cursor-pointer transition-all text-left ${
                      isDark
                        ? "border-gray-700 bg-gray-800 hover:border-pink-400 hover:bg-gray-700"
                        : "border-gray-200 bg-gray-50 hover:border-pink-300 hover:bg-pink-50"
                    }`}
                  >
                    <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${isDark ? "bg-gray-700" : "bg-white"}`}>
                      <i className={`${s.icon} text-pink-400 text-sm`}></i>
                    </div>
                    <div className="min-w-0">
                      <div className={`text-xs font-bold ${isDark ? "text-white" : "text-gray-900"}`}>{s.label}</div>
                      <div className={`text-[10px] ${isDark ? "text-gray-500" : "text-gray-400"} leading-tight`}>{s.desc}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}